import Core from "@/core/Core";

import type Assembly from "@/domains/Assemblies/Assembly";
import type Shipment from "@/domains/Shipments/Shipment";
import type TransportOrder from "@/domains/TransportOrders/TransportOrder";
import type OrderPosition from "@/domains/Orders/OrderPosition";
import type Product from "@/domains/Products/Product";
import type AbstractShipmentDocument from '@/domains/Abstract/AbstractShipmentDocument';

import AssembliesService from '@/domains/Assemblies/AssembliesService';
import ShipmentsService from '@/domains/Shipments/ShipmentsService';
import TransportOrdersService from '@/domains/TransportOrders/TransportOrdersService';
import TransportableDocument from "@/domains/TransportableDocuments/TransportableDocument";
import ProductsService from "@/domains/Products/ProductsService";
import ContextMenuItem from "@/lib/components/VContextMenu/ContextMenuItem";
import OrdersService from "@/domains/Orders/OrdersService";

/**
 * Общий сервис для отгрузочных документов (Assembly, Shipment, TransportOrder).
 * Маршрутизация по documentClass через статические свойства.
 */
class TransportableDocumentsService {

	/**
	 * Конфигурации типов документов.
	 * Геттеры вместо статических свойств — обращение к сервисам происходит
	 * в момент вызова, а не при инициализации модуля.
	 * Это решает проблему циклических зависимостей между сервисами.
	 */
	static get Assembly() {
		return {
			get: AssembliesService.get,
			edit: async (id) => Core.dialogs.AssemblyCard.showAsPromise({ id }),
			delete: (assembly: Assembly) => AssembliesService.deleteWithConfirm(assembly),
			showDialog:  async (assembly) => Core.dialogs.AssemblyCard.showAsPromise({ id: assembly.id }),
		};
	}

	static get Shipment() {
		return {
			get: ShipmentsService.get,
			edit: async (id) => Core.dialogs.ShipmentCard.showAsPromise({ id }),
			delete: (shipment: Shipment) => ShipmentsService.deleteWithConfirm(shipment),
			showDialog:  async (shipment) => Core.dialogs.ShipmentCard.showAsPromise({ id: shipment.id }),
		};
	}

	static get TransportOrder() {
		return {
			get: TransportOrdersService.get,
			edit: (id: number) => TransportOrdersService.showTransportOrderDialog(id),
			delete: (entity: TransportOrder) => TransportOrdersService.deleteWithConfirm(entity),
			showDialog: (entity: TransportOrder) => TransportOrdersService.showTransportOrderDialog(entity.id),
		};
	}

	static get OrderPosition() {
		return {
			get: OrdersService.getPosition,
			edit: async (id: number) => Core.dialogs.OrderPositionCard.showAsPromise({ id, isPreorder: false }),
			delete: (entity: OrderPosition) => OrdersService.deletePositionWithConfirm(entity),
			showDialog: (entity: OrderPosition) => OrdersService.showOrderPositionCard(entity),
		};
	}

	static get Product() {
		return {
			get: ProductsService.get,
			edit: async (id: number) => Core.dialogs.ProductCard.showAsPromise({ id }),
			delete: (entity: Product) => ProductsService.deleteWithConfirmation(entity),
			showDialog: (entity: Product) => ProductsService.showCard(entity.id),
		}
	}

	/**
	 * В методе ниже используется TransportableDocumentsService вместо this,
	 * потому что fromServer передаётся как колбэк (например в Utils.hydrateArray),
	 * и при таком вызове this теряет контекст (становится undefined).
	 */
	static fromServer(data: any): AbstractShipmentDocument {
		return TransportableDocument.fromServer(data)
		// return TransportableDocumentsService[data.documentClass].fromServer(data);
	}

	static get(document: AbstractShipmentDocument): Promise<void> {
		return TransportableDocumentsService[document.documentClass].get(document.id);
	}

	static async edit(document: TransportableDocument): Promise<boolean> {
		return TransportableDocumentsService[document.documentClass].edit(document.id);
	}

	static async delete(document: AbstractShipmentDocument) {
		return TransportableDocumentsService[document.documentClass].delete(document);
	}

	static showHistory(document: AbstractShipmentDocument): void {
		const entityTypeName = document.documentClass.charAt(0).toLowerCase() + document.documentClass.slice(1);

		Core.dialogs.FeedDialog.show({
			entityTypeName,
			entityId: document.id,
			showAddComment: false,
		});
	}

	// ============================================================
	// Контекстное меню
	// ============================================================

	/**
	 * Пункты контекстного меню транспортируемого документа для грида реестра.
	 * Передаётся в VDataGridV2 пропом :context-menu — грид сам рисует кебаб-колонку
	 * и меню по правому клику, и обновляет список после действия, только если
	 * callback пункта вернул true.
	 *
	 * Видимость пунктов «Создать ЗТК ...» зависит от documentClass конкретной строки —
	 * выражено через hidden-функцию (аналог прежних v-if по canCreate*).
	 */
	static get contextMenu(): ContextMenuItem<TransportableDocument>[] {
		return [
			new ContextMenuItem(
				'Открыть',
				'open_in_new',
				(document: TransportableDocument) => { return TransportableDocumentsService.edit(document); },
				null,   // disabled
				false,  // hidden
				true,   // dividerAfter
			),
			new ContextMenuItem(
				'Создать обычную ЗТК',
				'add_circle_outline',
				(document: TransportableDocument) => { return TransportOrdersService.showSingleSenderTransportOrderDialog(document); },
				null,   // disabled
				(document: TransportableDocument) => { return document.documentClass !== 'Assembly'; },  // hidden — только Assembly
			),
			new ContextMenuItem(
				'Создать конс. ЗТК',
				'add_circle_outline',
				(document: TransportableDocument) => { return TransportOrdersService.showConsolidatedTransportOrderDialog(document); },
				null,   // disabled
				// только Assembly / Shipment / TransportOrder
				(document: TransportableDocument) => { return document.canConsolidate === false; },  // hidden
			),
			new ContextMenuItem(
				'Создать ЗТК на забор',
				'add_circle_outline',
				(document: TransportableDocument) => { return TransportOrdersService.showOrderPositionPickupTransportOrderDialog(document); },
				null,   // disabled
				(document: TransportableDocument) => { return document.canPickupOrderPosition === false; },  // hidden
			),
			new ContextMenuItem(
				'Создать ЗТК на возврат',
				'add_circle_outline',
				(document: TransportableDocument) => { return TransportOrdersService.showReturnTransportOrderDialog(document); },
				null,   // disabled
				(document: TransportableDocument) => { return document.canReturnOrderPosition === false; },  // hidden
			),
			new ContextMenuItem(
				'Создать ЗТК на перемещение',
				'add_circle_outline',
				(document: TransportableDocument) => { return TransportOrdersService.showInternalTransportOrderDialog(document); },
				null,   // disabled
				(document: TransportableDocument) => { return document.canMoveInternalProduct === false; },  // hidden
				true,   // dividerAfter
			),
			new ContextMenuItem(
				'История действий',
				'history',
				(document: TransportableDocument) => { TransportableDocumentsService.showHistory(document); },
				null,   // disabled
				false,  // hidden
				true,   // dividerAfter
			),
			new ContextMenuItem(
				'Удалить',
				'delete',
				(document: TransportableDocument) => { return TransportableDocumentsService.delete(document); },
			),
		]
	}
}

export default TransportableDocumentsService;